import { detailedDiff } from 'deep-object-diff';
import { isEqual, get, set } from 'lodash';

const usePatchDiffer = <T extends object>(original: T, updated: T): Partial<T> => {
  const objectDiff = detailedDiff(original, updated);
  const patch: Partial<T> = {};

  // Handle added properties
  Object.keys(objectDiff.added).forEach((addedKey) => {
    const newValue = get(updated, addedKey);
    set(patch, addedKey, newValue);
  });

  // Handle updated properties
  const handleUpdated = (updatedObj: any, path: string[] = []) => {
    Object.keys(updatedObj).forEach((key) => {
      const currentPath = [...path, key];
      const originalValue = get(original, currentPath);
      const updatedValue = get(updated, currentPath);

      if (typeof updatedValue === 'object' && updatedValue !== null) {
        if (!isEqual(originalValue, updatedValue)) {
          // If the object changes, include the entire parent in the patch
          const parentPath = path.length > 0 ? path.join('.') : key;
          set(patch, parentPath, get(updated, parentPath));
        }
      } else if (Array.isArray(originalValue) || Array.isArray(updatedValue)) {
        // Reconstruct the array and include the parent if changes exist
        const reconstructedArray = updatedValue?.filter((item: any) => item !== undefined) || [];
        if (!isEqual(originalValue, reconstructedArray)) {
          const parentPath = path.length > 0 ? path.join('.') : key;
          set(patch, parentPath, get(updated, parentPath));
        }
      } else {
        // Set the updated value for primitives
        set(patch, currentPath, updatedValue);
      }
    });
  };

  handleUpdated(objectDiff.updated);

  // Handle deleted properties
  const handleDeletion = (deletedObj: any, path: string[] = []) => {
    Object.keys(deletedObj).forEach((key) => {
      const currentPath = [...path, key];
      const originalValue = get(original, currentPath);
      const updatedValue = get(updated, currentPath);

      if (Array.isArray(originalValue)) {
        // Reconstruct the array without removed items
        const remainingItems = updatedValue?.filter((item: any) => item !== undefined) || [];
        const parentPath = path.length > 0 ? path.join('.') : key;
        set(patch, parentPath, get(updated, parentPath));
      } else if (updatedValue === undefined) {
        // If the value is truly deleted, set it to undefined
        set(patch, currentPath, undefined);
      } else if (typeof deletedObj[key] === 'object' && deletedObj[key] !== null) {
        // If it's a nested object, process recursively
        handleDeletion(deletedObj[key], currentPath);
      }
    });
  };

  handleDeletion(objectDiff.deleted);

  return patch;
};

export default usePatchDiffer;
